<script setup lang="ts">
import * as d3 from "d3";
import { nextTick, onBeforeUnmount, onMounted, useTemplateRef, watch } from "vue";

const COLOR_COUNT = 8;

const {
  traces,
  traceNames = undefined,
  binsNames,
  barLabels,
  margin = { top: 20, right: 0, bottom: 10, left: 0 },
  xAxisTickFormat = ".0f",
  xAxisLegend = "",
} = defineProps<{
  traces: number[][];
  traceNames?: string[];
  binsNames?: string[];
  barLabels?: (string | undefined)[][];
  margin?: { top: number; right: number; bottom: number; left: number };
  xAxisTickFormat?: string;
  xAxisLegend?: string;
}>();

const plot = useTemplateRef<SVGElement>("plot");

function updatePlot() {
  if (plot.value) {
    const width = plot.value.clientWidth;
    const height = plot.value.clientHeight;
    const svg = d3.select(plot.value);
    const axesG = svg.select<SVGGElement>(".axes");
    const axisY = axesG.select<SVGGElement>(".y");
    const axisX = axesG.select<SVGGElement>(".x");
    const barsG = svg.select<SVGGElement>(".bars");

    // Y axis (indices)
    const maxLength = d3.max(traces, (d) => d.length) ?? 0;
    const indices = d3.range(maxLength);
    const yScale = d3
      .scaleBand<number>()
      .range([margin.top, height - margin.bottom])
      .domain(indices)
      .paddingInner(0.2);

    axisY
      .attr("transform", `translate(${margin.left}, 0)`)
      .transition()
      .duration(200)
      .call(d3.axisLeft(yScale).tickFormat((d) => binsNames?.[d] ?? d.toString()));

    // compute dynamic left margin from longest Y tick label
    const yTickTexts = axisY.selectAll<SVGTextElement, unknown>(".tick text");
    let maxLabelWidth = 0;
    yTickTexts.each(function () {
      const bbox = (this as SVGTextElement).getBBox();
      if (bbox.width > maxLabelWidth) {
        maxLabelWidth = bbox.width;
      }
    });
    const leftMargin = Math.max(margin.left, maxLabelWidth + 10);

    // X axis (values) using dynamic left margin
    const allValues = traces.flatMap((d) => d);
    const [, xMax] = d3.extent(allValues) as [number, number];
    const xScale = d3
      .scaleLinear()
      .range([leftMargin, width - margin.right - leftMargin])
      .domain([0, xMax]);

    axisX
      .attr("transform", `translate(0, ${height - margin.bottom})`)
      .transition()
      .duration(200)
      .call(
        d3
          .axisBottom(xScale)
          .tickSize(-height + margin.top)
          .tickFormat(d3.format(xAxisTickFormat))
      );

    // also move Y axis to match new left margin
    axisY.attr("transform", `translate(${leftMargin}, 0)`);

    // bars (horizontal, zipped by index across traces)
    type BarDatum = { index: number; traceIndex: number; value: number };

    const barsData: BarDatum[] = [];
    for (let index = 0; index < maxLength; index++) {
      for (let traceIndex = 0; traceIndex < traces.length; traceIndex++) {
        const trace = traces[traceIndex];
        if (!trace) continue;
        const value = trace[index];
        if (value !== undefined) {
          barsData.push({ index, traceIndex, value });
        }
      }
    }

    // grouped-by-index band on Y, then sub-band by trace index
    const ySubScale = d3
      .scaleBand<number>()
      .domain(d3.range(traces.length))
      .range([0, yScale.bandwidth()]);

    svg.attr("style", `--bar-label-width: ${ySubScale.bandwidth()}px`);
    const safeY = (index: number) => yScale(index) ?? margin.top;
    const safeSubY = (traceIndex: number) => ySubScale(traceIndex) ?? 0;

    const xZero = xScale(0);

    const sortedBarsData = barsData.sort(
      (a, b) => a.index - b.index || a.traceIndex - b.traceIndex
    );

    const barsSelection = barsG
      .selectAll<SVGRectElement, BarDatum>(".bar")
      .data(sortedBarsData, (d: any) => `${d.index}-${d.traceIndex}`);

    const enter = barsSelection.enter().append("rect").attr("class", "bar");

    const merged = enter.merge(barsSelection);
    merged
      .transition()
      .duration(200)
      .attr("y", (d: BarDatum) => safeY(d.index) + safeSubY(d.traceIndex))
      .attr("x", xScale(0))
      .attr("height", ySubScale.bandwidth())
      .attr("width", (d: BarDatum) => Math.abs(xScale(d.value) - xZero))
      .attr("class", (d: BarDatum) => `bar trace-${d.traceIndex % COLOR_COUNT}`);

    // labels (optional, per bar)
    const labelsSelection = barsG
      .selectAll<SVGTextElement, BarDatum>(".bar-label")
      .data(sortedBarsData, (d: any) => `${d.index}-${d.traceIndex}`);

    labelsSelection.exit().remove();

    const labelsEnter = labelsSelection.enter().append("text").attr("class", "bar-label");

    labelsEnter
      .merge(labelsSelection)
      .transition()
      .duration(200)
      .text((d: BarDatum) => barLabels?.[d.traceIndex]?.[d.index] ?? "")
      .attr("x", (d: BarDatum) => Math.abs(xScale(d.value) - xZero) + xScale(0) - 10)
      .attr(
        "y",
        (d: BarDatum) => safeY(d.index) + safeSubY(d.traceIndex) + ySubScale.bandwidth() / 2
      )
      .attr("text-anchor", "end")
      .attr("dominant-baseline", "middle")
      .attr("class", (d: BarDatum) => `bar-label trace-${d.traceIndex % COLOR_COUNT}`);

    barsSelection.exit().remove();
  }
}

const resizeObserver = new ResizeObserver(() => {
  updatePlot();
});

watch(
  () => [traces, binsNames, barLabels],
  () => {
    updatePlot();
  }
);

onMounted(async () => {
  // be sure that container has its full size already
  await nextTick();
  if (plot.value) {
    updatePlot();
    resizeObserver.observe(plot.value);
  }
});

onBeforeUnmount(() => {
  if (plot.value) {
    resizeObserver.unobserve(plot.value);
  }
});
</script>

<template>
  <div class="histogram-plot">
    <svg ref="plot">
      <g class="bars"></g>
      <g class="axes">
        <g class="x"></g>
        <g class="y"></g>
      </g>
    </svg>
    <div v-if="xAxisLegend.length > 0" class="x-legend text-xs-regular">
      {{ xAxisLegend }}
    </div>
    <div v-if="traceNames && traceNames.length > 0" class="traces-legend">
      <div
        v-for="(trace, index) in traceNames"
        :key="index"
        class="trace-legend text-xs-regular"
        :class="[`trace-${index % COLOR_COUNT}`]"
      >
        <div>
          {{ trace }}
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.histogram-plot {
  --plotly-green: #00cc96;
  --plotly-purple: #ab63fa;
  --plotly-light-orange: #ffa15a;
  --plotly-cyan: #19d3f3;
  --plotly-pink: #ff6692;
  --plotly-light-green: #b6e880;

  display: flex;
  height: 100%;
  flex-direction: column;

  svg {
    width: 100%;
    flex: 1;
  }

  text {
    font-family: GeistMono, monospace;
  }

  g.axes {
    path.domain {
      stroke: transparent;
      stroke-width: 0;
    }

    .tick line {
      opacity: 0.2;
      stroke: var(--content-secondary);
      stroke-dasharray: 2;
      stroke-width: var(--stroke-width-md);
    }

    .tick text {
      fill: var(--content-secondary);
    }
  }

  .bar {
    stroke-width: 0;
  }

  .bar-label {
    filter: brightness(150%);
    font-size: min(var(--numbers-12), var(--bar-label-width));
  }

  /* Color-blind friendly palette inspired by Plotly */
  .trace-0 {
    fill: var(--color-brand-blue-300); /* similar to Plotly #636EFA */
  }

  .trace-1 {
    fill: var(--color-brand-orange-300); /* similar to Plotly #EF553B */
  }

  .trace-2 {
    fill: var(--plotly-green);
  }

  .trace-3 {
    fill: var(--plotly-purple);
  }

  .trace-4 {
    fill: var(--plotly-light-orange);
  }

  .trace-5 {
    fill: var(--plotly-cyan);
  }

  .trace-6 {
    fill: var(--plotly-pink);
  }

  .trace-7 {
    fill: var(--plotly-light-green);
  }

  .x-legend {
    padding-top: var(--numbers-8);
    color: var(--content-secondary);
    text-align: center;
  }

  .traces-legend {
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    padding: var(--numbers-8);
    color: var(--content-tertiary);
    gap: var(--numbers-8);

    > div {
      position: relative;
      padding-left: var(--numbers-20);

      &::before {
        position: absolute;
        top: 0;
        left: 0;
        width: var(--numbers-16);
        height: var(--numbers-16);
        border-radius: var(--numbers-16);
        content: " ";
      }

      &.trace-0::before {
        background-color: var(--color-brand-blue-300);
      }

      &.trace-1::before {
        background-color: var(--color-brand-orange-300);
      }

      &.trace-2::before {
        background-color: var(--plotly-green);
      }

      &.trace-3::before {
        background-color: var(--plotly-purple);
      }

      &.trace-4::before {
        background-color: var(--plotly-light-orange);
      }

      &.trace-5::before {
        background-color: var(--plotly-cyan);
      }

      &.trace-6::before {
        background-color: var(--plotly-pink);
      }

      &.trace-7::before {
        background-color: var(--plotly-light-green);
      }
    }
  }
}
</style>
